import type { FC } from 'react';
import { Col, Row } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { AvatarSkeleton, ButtonSkeleton } from '@/shared/components/skeletons';

import { notificationMenuItemStyle } from '../NotificationMenuItem/styles';

interface NotificationMenuItemSkeletonProps {
    count?: number;
    label?: string;
}

const headlineWidths = [
    ['28%', '15%', '34%'],
    ['34%', '12%', '28%'],
    ['24%', '18%', '38%'],
] as const;
const metadataWidths = [
    ['25%', '12%'],
    ['30%', '10%'],
    ['22%', '14%'],
] as const;

/** Mirrors the two-line notification menu-item geometry for initial and paginated loading. */
const NotificationMenuItemSkeleton: FC<NotificationMenuItemSkeletonProps> = ({
    count = 6,
    label = 'Loading notifications',
}) => {
    const { token } = useAntDesignTheme();

    return (
        <div aria-label={label} aria-live='polite' data-notification-menu-skeleton='true' role='status'>
            {Array.from({ length: count }, (_, index) => {
                const headline = headlineWidths[index % headlineWidths.length];
                const metadata = metadataWidths[index % metadataWidths.length];

                return (
                    <Row
                        align='top'
                        data-notification-menu-skeleton-item='true'
                        gutter={[8, 0]}
                        key={index}
                        wrap={false}
                        style={{
                            ...notificationMenuItemStyle,
                            borderBottom: `1px solid ${token.colorBorder}`,
                            margin: 0,
                            minWidth: 0,
                            width: '100%',
                        }}
                    >
                        <Col flex='none'>
                            <span
                                style={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    height: '1.25rem',
                                    justifyContent: 'center',
                                }}
                            >
                                <AvatarSkeleton size={6} />
                            </span>
                        </Col>
                        <Col flex='auto' style={{ minWidth: 0 }}>
                            <div
                                data-notification-menu-skeleton-headline='true'
                                style={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    gap: '0.25rem',
                                    height: '1.25rem',
                                    minWidth: 0,
                                }}
                            >
                                {headline.map((width, headlineIndex) => (
                                    <ButtonSkeleton
                                        key={headlineIndex}
                                        size='small'
                                        style={{
                                            display: 'block',
                                            flex: headlineIndex === headline.length - 1 ? '1 1 auto' : 'none',
                                            height: headlineIndex === 1 ? '0.75rem' : '1.125rem',
                                            maxWidth: headlineIndex === headline.length - 1 ? '9rem' : undefined,
                                            width,
                                        }}
                                    />
                                ))}
                            </div>
                            <div
                                data-notification-menu-skeleton-metadata='true'
                                style={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    gap: '0.75rem',
                                    height: '1.25rem',
                                    marginTop: '0.25rem',
                                    minWidth: 0,
                                }}
                            >
                                {metadata.map((width, metadataIndex) => (
                                    <ButtonSkeleton
                                        key={metadataIndex}
                                        size='small'
                                        style={{ display: 'block', height: '0.75rem', width }}
                                    />
                                ))}
                            </div>
                        </Col>
                        <Col flex='none'>
                            <span
                                style={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    height: '1.25rem',
                                    justifyContent: 'center',
                                }}
                            >
                                <ButtonSkeleton
                                    shape='square'
                                    size='small'
                                    style={{ height: '1.25rem', minWidth: '1.25rem', width: '1.25rem' }}
                                />
                            </span>
                        </Col>
                    </Row>
                );
            })}
        </div>
    );
};

export default NotificationMenuItemSkeleton;
export type { NotificationMenuItemSkeletonProps };
